/**
 * Window-level guard so a file dropped OUTSIDE a FileDropZone does nothing.
 *
 * Customer review 11 Sep (user request: drag and drop on every upload). A drop
 * that misses its target falls through to the browser's default, which opens
 * the file in the tab and throws away whatever the user had typed. Mounted
 * once in the app shell; it only acts on OS file drags that no zone (and no
 * page-level handler, e.g. the Document Manager's whole-page drop) has
 * already claimed, so real drop targets are untouched.
 */
import { useEffect } from 'react';
import { isFileDrag } from './fileDropValidation';

/** Marker attribute every FileDropZone renders. */
const ZONE_SELECTOR = '[data-file-drop-zone]';

function insideZone(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(ZONE_SELECTOR) !== null;
}

/**
 * Renders nothing; installs the window dragover/drop listeners for its lifetime.
 *
 * @returns null
 */
export default function FileDropGuard(): null {
  useEffect(() => {
    const onDragOver = (e: DragEvent) => {
      if (!isFileDrag(e.dataTransfer) || e.defaultPrevented || insideZone(e.target)) return;
      e.preventDefault();
      // "none" shows the no-drop cursor and stops the drop event firing.
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
    };
    const onDrop = (e: DragEvent) => {
      if (!isFileDrag(e.dataTransfer) || insideZone(e.target)) return;
      e.preventDefault();
    };
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, []);
  return null;
}
